/**
 * @file 引用串编辑交互（知识库 / Chart：点击整段选中 + 删除）
 */
import type { IInlineMentionRange } from './inlineMentionRanges';

/** 选区右边界：有尾随空格则含空格 */
export const mentionEndOffset = (
  value: string,
  range: IInlineMentionRange,
): number => (value[range.end] === ' ' ? range.end + 1 : range.end);

export interface IMentionHitResult {
  range: IInlineMentionRange;
  element: HTMLElement;
  isRemoveAction: boolean;
}

/** 透过 textarea 命中 mirror token */
export function hitTestMention(
  textarea: HTMLTextAreaElement,
  ranges: IInlineMentionRange[],
  clientX: number,
  clientY: number,
): IMentionHitResult | null {
  if (!ranges.length) return null;
  const mirror = textarea.parentElement?.querySelector(
    '.dba-qa-mention-mirror',
  ) as HTMLElement | null;
  textarea.style.pointerEvents = 'none';
  if (mirror) mirror.style.pointerEvents = 'auto';
  const target = document.elementFromPoint(clientX, clientY) as HTMLElement | null;
  if (mirror) mirror.style.pointerEvents = '';
  textarea.style.pointerEvents = '';

  const tokenEl = target?.closest?.(
    '[data-mention-key]',
  ) as HTMLElement | null;
  if (tokenEl?.dataset?.mentionKey) {
    const range = ranges.find((item) => item.key === tokenEl.dataset.mentionKey);
    if (range) {
      return {
        range,
        element: tokenEl,
        isRemoveAction: Boolean(
          target?.closest?.('[data-mention-action="remove"]'),
        ),
      };
    }
  }

  if (!mirror) return null;
  const tokenNodes = mirror.querySelectorAll('[data-mention-key]');
  for (const node of Array.from(tokenNodes)) {
    const el = node as HTMLElement;
    const key = el.dataset.mentionKey;
    if (!key) continue;
    const range = ranges.find((item) => item.key === key);
    if (!range) continue;
    const rect = el.getBoundingClientRect();
    if (
      clientX >= rect.left
      && clientX <= rect.right
      && clientY >= rect.top
      && clientY <= rect.bottom
    ) {
      const removeEl = el.querySelector('[data-mention-action="remove"]');
      let isRemoveAction = false;
      if (removeEl) {
        const rr = removeEl.getBoundingClientRect();
        isRemoveAction =
          clientX >= rr.left
          && clientX <= rr.right
          && clientY >= rr.top
          && clientY <= rr.bottom;
      }
      return { range, element: el, isRemoveAction };
    }
  }
  return null;
}

/** 整段选中 mention（含尾随空格） */
export function selectMentionRange(
  el: HTMLTextAreaElement,
  range: IInlineMentionRange,
  value: string,
): void {
  const end = mentionEndOffset(value, range);
  el.focus();
  el.setSelectionRange(range.start, end);
}

export interface IAtomicDeleteResult {
  nextValue: string;
  cursor: number;
}

export function deleteMentionByRange(
  value: string,
  range: IInlineMentionRange,
): IAtomicDeleteResult {
  const deleteEnd = mentionEndOffset(value, range);
  return {
    nextValue: value.slice(0, range.start) + value.slice(deleteEnd),
    cursor: range.start,
  };
}

export function atomicDeleteMention(
  el: HTMLTextAreaElement,
  ranges: IInlineMentionRange[],
  value: string,
  key: string,
): IAtomicDeleteResult | null {
  if (key !== 'Backspace' && key !== 'Delete') return null;
  if (!ranges.length) return null;

  const curStart = el.selectionStart ?? 0;
  const curEnd = el.selectionEnd ?? curStart;

  if (curStart !== curEnd) {
    const whole = ranges.find((r) => {
      const mentionEnd = mentionEndOffset(value, r);
      return (
        curStart === r.start
        && (curEnd === mentionEnd || curEnd === r.end)
      );
    });
    if (!whole) return null;

    if (whole.kind === 'knowledge' && whole.label.includes('、')) {
      const deleteEnd = mentionEndOffset(value, whole);
      const lastSep = whole.label.lastIndexOf('、');
      const shortened = whole.label.substring(0, lastSep);
      return {
        nextValue:
          `${value.slice(0, whole.start)}${shortened} ${value.slice(deleteEnd)}`,
        cursor: whole.start + shortened.length + 1,
      };
    }
    return deleteMentionByRange(value, whole);
  }

  let hit: IInlineMentionRange | null = null;
  if (key === 'Backspace') {
    hit =
      ranges.find((r) => curStart === mentionEndOffset(value, r)) || null;
  } else {
    hit = ranges.find((r) => curStart === r.start) || null;
  }
  if (!hit) return null;

  if (hit.kind === 'knowledge' && hit.label.includes('、')) {
    const deleteEnd = mentionEndOffset(value, hit);
    const lastSep = hit.label.lastIndexOf('、');
    const shortened = hit.label.substring(0, lastSep);
    return {
      nextValue:
        `${value.slice(0, hit.start)}${shortened} ${value.slice(deleteEnd)}`,
      cursor: hit.start + shortened.length + 1,
    };
  }

  return deleteMentionByRange(value, hit);
}
